<template>
  <v-dialog
    :model-value="modelValue"
    max-width="520"
    scrim="rgba(15, 23, 32, 0.55)"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card class="lms-remote-dialog">
      <v-card-title class="lms-remote-dialog__title">{{ title }}</v-card-title>
      <v-card-subtitle v-if="subtitle" class="lms-remote-dialog__sub">{{ subtitle }}</v-card-subtitle>
      <v-card-text>
        <v-alert v-if="error" type="error" variant="tonal" density="compact" class="mb-3">
          {{ error }}
        </v-alert>
        <div
          v-for="field in fields"
          :key="field.name"
          class="lms-remote-dialog__field"
        >
          <template v-if="field.type === 'select'">
            <v-select
              v-model="local[field.name]"
              :items="field.choices || []"
              item-title="label"
              item-value="value"
              :label="field.label"
              :required="field.required"
              density="comfortable"
              variant="outlined"
              hide-details="auto"
            />
          </template>
          <template v-else-if="field.type === 'number'">
            <v-text-field
              v-model="local[field.name]"
              :label="field.label"
              type="number"
              :min="field.min"
              :max="field.max"
              :required="field.required"
              density="comfortable"
              variant="outlined"
              hide-details="auto"
            />
          </template>
          <template v-else-if="field.type === 'date'">
            <v-text-field
              v-model="local[field.name]"
              :label="field.label"
              type="date"
              :required="field.required"
              density="comfortable"
              variant="outlined"
              hide-details="auto"
            />
          </template>
          <template v-else-if="field.type === 'textarea'">
            <v-textarea
              v-model="local[field.name]"
              :label="field.label"
              :required="field.required"
              rows="4"
              auto-grow
              density="comfortable"
              variant="outlined"
              hide-details="auto"
            />
          </template>
          <template v-else>
            <v-text-field
              v-model="local[field.name]"
              :label="field.label"
              :required="field.required"
              density="comfortable"
              variant="outlined"
              hide-details="auto"
            />
          </template>
        </div>
      </v-card-text>
      <v-card-actions class="lms-remote-dialog__actions">
        <v-btn variant="text" :disabled="saving" @click="close">Cancel</v-btn>
        <v-spacer />
        <v-btn color="primary" variant="flat" :loading="saving" @click="submit">
          {{ submitLabel }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { reactive, ref, watch } from 'vue'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  title: { type: String, default: '' },
  subtitle: { type: String, default: '' },
  submitLabel: { type: String, default: 'Save' },
  action: { type: String, required: true },
  method: { type: String, default: 'post' },
  paramRoot: { type: String, default: '' },
  fields: { type: Array, default: () => [] },
  values: { type: Object, default: () => ({}) },
  hidden: { type: Object, default: () => ({}) }
})

const emit = defineEmits(['update:modelValue', 'saved'])

const local = reactive({})
const saving = ref(false)
const error = ref('')

watch(
  () => [props.modelValue, props.values, props.fields],
  () => {
    if (!props.modelValue) return
    error.value = ''
    Object.keys(local).forEach((k) => { delete local[k] })
    ;(props.fields || []).forEach((f) => {
      const fromValues = props.values?.[f.name]
      local[f.name] = fromValues != null && fromValues !== ''
        ? fromValues
        : (f.value ?? (f.choices?.[0]?.value ?? ''))
    })
  },
  { immediate: true, deep: true }
)

function close () {
  emit('update:modelValue', false)
}

function csrfToken () {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
}

function buildBody () {
  const fd = new FormData()
  Object.entries(props.hidden || {}).forEach(([k, v]) => fd.append(k, v == null ? '' : String(v)))
  ;(props.fields || []).forEach((f) => {
    const key = props.paramRoot ? `${props.paramRoot}[${f.name}]` : f.name
    const val = local[f.name]
    fd.append(key, val == null ? '' : String(val))
  })
  const method = (props.method || 'post').toLowerCase()
  if (method !== 'post' && method !== 'get') {
    fd.append('_method', method)
  }
  return fd
}

async function submit () {
  saving.value = true
  error.value = ''
  try {
    const method = (props.method || 'post').toLowerCase()
    const res = await fetch(props.action, {
      method: method === 'get' ? 'GET' : 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'X-CSRF-Token': csrfToken(),
        'X-Requested-With': 'XMLHttpRequest',
        'X-LMS-Vue': '1'
      },
      body: method === 'get' ? undefined : buildBody()
    })
    if (res.redirected) {
      emit('saved')
      close()
      window.location.href = res.url
      return
    }
    const contentType = res.headers.get('content-type') || ''
    if (contentType.includes('application/json')) {
      const json = await res.json()
      if (!res.ok || json.ok === false) {
        error.value = json.error || json.message || 'Could not save'
        return
      }
      emit('saved', json)
      close()
      if (json.redirect) {
        window.location.href = json.redirect
      } else {
        window.location.reload()
      }
      return
    }
    if (!res.ok) {
      error.value = `Request failed (${res.status})`
      return
    }
    emit('saved')
    close()
    window.location.reload()
  } catch (e) {
    error.value = e.message || 'Could not save'
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.lms-remote-dialog {
  border-radius: 16px !important;
}
.lms-remote-dialog__title {
  font-family: Outfit, 'Source Sans 3', sans-serif;
  font-weight: 700;
  font-size: 1.25rem;
  letter-spacing: -0.02em;
  padding-bottom: 0 !important;
}
.lms-remote-dialog__sub {
  opacity: 0.8;
}
.lms-remote-dialog__field + .lms-remote-dialog__field {
  margin-top: 12px;
}
.lms-remote-dialog__actions {
  padding: 8px 16px 16px !important;
}
</style>
